import type {
  MergeHouseholdsInput,
  RenameHouseholdInput,
  SetPasswordInput,
} from '@room-mates/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../shared/api/queryKeys.js';
import { useApi } from '../../shared/auth/useAuth.js';

/**
 * Administrative writes move accounts between households, so both listings are
 * refreshed after every one of them rather than trying to guess which changed.
 */
function useAdminInvalidation(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.admin.users });
    await queryClient.invalidateQueries({ queryKey: queryKeys.admin.households });
  };
}

export function useAdminUsers() {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.admin.users, queryFn: () => api.admin.users() });
}

export function useAdminHouseholds() {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.admin.households, queryFn: () => api.admin.households() });
}

export function useSetUserPassword() {
  const api = useApi();
  return useMutation({
    mutationFn: ({ userId, input }: { userId: string; input: SetPasswordInput }) =>
      api.admin.setPassword(userId, input),
  });
}

export function useDetachHousehold() {
  const api = useApi();
  const invalidate = useAdminInvalidation();
  return useMutation({
    mutationFn: (userId: string) => api.admin.detachHousehold(userId),
    onSuccess: invalidate,
  });
}

export function useRenameHousehold() {
  const api = useApi();
  const invalidate = useAdminInvalidation();
  return useMutation({
    mutationFn: ({ householdId, input }: { householdId: string; input: RenameHouseholdInput }) =>
      api.admin.renameHousehold(householdId, input),
    onSuccess: invalidate,
  });
}

export function useMergeHouseholds() {
  const api = useApi();
  const invalidate = useAdminInvalidation();
  return useMutation({
    mutationFn: ({ householdId, input }: { householdId: string; input: MergeHouseholdsInput }) =>
      api.admin.mergeHouseholds(householdId, input),
    onSuccess: invalidate,
  });
}
